/**
 * Locating a project on disk and reading its artifact files.
 *
 * Reads are "optional": a missing file resolves to `null`, which the validators
 * treat as "artifact missing" (a validation failure with fix guidance) rather
 * than a hard error. A genuinely unreadable project directory is a `UsageError`.
 *
 * @module
 */
import { Effect, FileSystem, Path } from "effect"
import { UsageError } from "./errors"

/** Resolve `--dir` to an absolute path, failing if it does not exist. */
export const resolveProjectDir = (
  dir: string,
): Effect.Effect<string, UsageError, FileSystem.FileSystem | Path.Path> =>
  Effect.gen(function* () {
    const path = yield* Path.Path
    const fs = yield* FileSystem.FileSystem
    const resolved = path.resolve(dir)
    const exists = yield* fs.exists(resolved).pipe(Effect.orElseSucceed(() => false))
    if (!exists) {
      return yield* new UsageError({ reason: `Project directory not found: ${resolved}` })
    }
    return resolved
  })

/** Read a project-relative file, returning `null` if it does not exist. */
export const readArtifact = (
  projectDir: string,
  relativePath: string,
): Effect.Effect<string | null, UsageError, FileSystem.FileSystem | Path.Path> =>
  Effect.gen(function* () {
    const path = yield* Path.Path
    const fs = yield* FileSystem.FileSystem
    const full = path.join(projectDir, relativePath)
    const exists = yield* fs.exists(full)
    if (!exists) return null
    return yield* fs.readFileString(full)
  }).pipe(
    Effect.catchTag("PlatformError", (cause) =>
      Effect.fail(new UsageError({ reason: `Could not read ${relativePath}: ${cause.message}` })),
    ),
  )
